import type { KeyboardEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import type { Config } from './../wix-react-player';
import { ReactPlayer } from './../wix-react-player';
import { isSSR } from './../wix-rich-content-common';

import { pauseTrigger } from './constants';
import { REACT_PLAYER_FILE } from './data-hooks';

const VIMEO_SDK_URL = 'https://player.vimeo.com/api/player.js';
const VIMEO_GLOBAL = 'Vimeo';

export type ReactPlayerWrapperProps =
  | {
      url: string;
      onProgress: (state: { played: number; playedSeconds: number }) => void;
      progressInterval: number;
      config: Config;
      width: string;
      height: string;
      muted: boolean;
      isLoaded: boolean;
      controls: boolean;
      loop: boolean;
      autoPlay: boolean;
      isCustomVideo: boolean;
      className: string;
      onReady?: (playerRef: React.MutableRefObject<ReactPlayer | null>) => void;
      onDuration?: (duration: number) => void;
      disabled?: boolean;
      styles?: object;
      onContextMenu: (e: React.MouseEvent) => void;
    } & (
      | {
          isLight: true;
          thumbnail: string | undefined;
        }
      | { isLight: false }
    );

export const ReactPlayerWrapper = ({
  styles,
  disabled,
  onReady,
  onDuration,
  onProgress,
  url,
  width,
  height,
  progressInterval,
  config,
  muted,
  isLoaded,
  controls,
  className,
  onContextMenu,
  loop,
  isCustomVideo,
  autoPlay,
  ...rest
}: ReactPlayerWrapperProps) => {
  const playerRef = useRef<ReactPlayer | null>(null);
  const playerWrapper = useRef<HTMLDivElement | null>(null);
  const [vimeoLoaded, setVimeoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);

  const isVimeoAndRequireJS = () => {
    const isVimeo = !isSSR() && isVimeoUrl(url) && !window[VIMEO_GLOBAL];
    return isVimeo && window.requirejs;
  };

  useEffect(() => {
    if (isVimeoAndRequireJS()) {
      window.require([VIMEO_SDK_URL], (player: $TSFixMe) => {
        window.Vimeo = { Player: player } as unknown as typeof window.Vimeo;
        setVimeoLoaded(true);
      });
    }

    const autoPlayCleanup = autoPlay ? handleAutoPlay() : undefined;

    return () => {
      autoPlayCleanup?.();
    };
  }, []);

  const handleAutoPlay = () => {
    if (!window?.IntersectionObserver || !playerRef.current || !playerWrapper.current) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const startAutoPlay = entry.isIntersecting && !disabled && !isAutoPlayPaused;
        if (startAutoPlay) {
          if (rest.isLight && playerRef.current?.state.showPreview) {
            playerRef.current.handleClickPreview();
          }
          handlePlay();
        } else {
          handlePause(pauseTrigger.scroll);
        }
      });
    });
    intersectionObserver.observe(playerWrapper.current);

    return () => {
      intersectionObserver.disconnect();
    };
  };

  const handlePlay = () => {
    if (!isPlaying) {
      setIsPlaying(true);
    }
  };

  const handlePause = (trigger?: string) => {
    if (isPlaying) {
      const isAutoPlayPaused = trigger !== pauseTrigger.scroll;
      setIsPlaying(false);
      setIsAutoPlayPaused(isAutoPlayPaused);
    }
  };

  const onWrapperClick = () => {
    if (isCustomVideo && !controls) {
      isPlaying ? handlePause() : handlePlay();
    }
  };

  if (!vimeoLoaded && isVimeoAndRequireJS()) {
    return null;
  }

  const lightWeightMode = rest.isLight && rest.thumbnail;

  return (
    <div ref={playerWrapper}>
      <ReactPlayer
        previewAriaLabel="Play video"
        playing={isPlaying && !disabled}
        onPlay={handlePlay}
        onPause={handlePause}
        controls={controls}
        style={styles}
        light={lightWeightMode}
        onReady={() => {
          if (lightWeightMode) {
            handlePlay();
          }

          onReady?.(playerRef);
        }}
        url={url}
        width={width}
        height={height}
        onDuration={onDuration}
        onProgress={onProgress}
        progressInterval={progressInterval}
        muted={muted}
        data-loaded={isLoaded}
        className={className}
        onContextMenu={onContextMenu}
        ref={playerRef}
        loop={loop}
        config={{
          ...config,
          file: {
            ...config?.file,
            // eslint-disable-next-line no-new-object
            attributes: new Object({
              ...config?.file?.attributes,
              'data-hook': REACT_PLAYER_FILE,
              onClick: onWrapperClick,
              onKeyDown: (e: KeyboardEvent<HTMLVideoElement>) => {
                if (e.code === 'Enter' || e.code === 'Space') {
                  onWrapperClick();
                }
              },
            }),
          },
        }}
      />
    </div>
  );
};

function isVimeoUrl(url: string) {
  //this is for react player, regex taken from there https://github.com/CookPete/react-player/blob/ad5d6df62635497137a184cf93a9c43ba6b08fbb/src/players/Vimeo.js#L8
  const MATCH_URL = /vimeo\.com\/.+/;
  const MATCH_FILE_URL = /vimeo\.com\/external\/[0-9]+\..+/;
  return !MATCH_FILE_URL.test(url) && MATCH_URL.test(url);
}
